/**
 * **文档同步** —— 让「多个节点图窗口看同一份数据」这件事真的成立。
 *
 * ## 问题：Vue Flow 的变更是「就地改」，Vue 看不见
 *
 * 实测（`scripts/verify-area-spike.ts` 里的往返实验）：
 *
 * ```js
 * const out = applyNodeChanges([{ id: 'p1', type: 'select', selected: true }], nodes)
 * out === nodes      // → true   ⚠️ 返回的是**同一个数组**
 * out[0] === nodes[0] // → true   ⚠️ 而且是**同一个对象**
 *
 * applyNodeChanges([{ id: 'p1', type: 'position', … }], nodes)
 * out === nodes      // → true   ⚠️ 位置变更也是同一个数组
 * ```
 *
 * Vue Flow 内部是按「有一个宿主，而且宿主手里就是这些对象」设计的：
 * 它就地改 `node.selected` / `node.position`，**不换数组**，
 * 所以它自己的 `watch(store.nodes)` 不会触发，也就**不会写回** `v-model`
 * 那一份 ref。
 *
 * 单个窗口时这没问题（父组件手里的就是同一批对象，改了就看见了）。
 * 但每个 `<VueFlow>` 在挂载时都会走一遍 `parseNode`，**为每个实例生成
 * 自己的节点对象** —— 于是「就地改」只改了那一个窗口里的那一份：
 *
 *   · 选中态各窗口不一致（点空白只能清掉当前窗口的）；
 *   · 拖动节点另一个窗口不动；
 *   · 谁下次把文档重新发一遍，就把旧状态**盖回去**
 *     （用户实测：「点空白处会恢复选择新窗口的选中状态」）。
 *
 * ## 解法：文档单写，且每次发布都**强制换引用**
 *
 * 这条路必须由宿主来走（Vue Flow 管不到）：
 *
 * 1. `:nodes="nodes"`（**单向**，不用 `v-model` —— 一个 ref 不能有两个写者）；
 * 2. `@nodes-change` 交给下面这两个函数，把变更合并进文档；
 * 3. 合并之后**必须让数组引用真的变化**，否则 `nodes.value = …` 对 Vue
 *    来说是空操作，其他窗口的 prop watcher 不会触发、也就不会重新同步。
 *
 * ## 2027：第 3 条从"补偿"变成了"契约"
 *
 * 上面那段实测（库对 `select` 就地改）是**库的行为**。现在合并逻辑
 * 是我们自己的（`graph/flowChanges.ts`）：它**保证**换数组 + 被碰到的
 * 元素换对象、其余复用 —— 所以这里的两个 `publish*` 退化成薄封装，
 * 那层"按 id 克隆"的补救代码删掉了。
 *
 * ⚠️ 换来的不只是少几行：**换引擎时这一层不用动**，"如何把变更变成
 * 可观察的变化"是我们自己的决定，不再依赖某个库的小版本行为。
 */
import { applyEdgeChanges, applyNodeChanges } from './flowChanges'
import type { FlowEdgeChange, FlowNodeChange } from './flowChanges'
import type { FlowEdge, FlowNode } from './flowTypes'

/**
 * 把节点变更合并进文档 —— **保证返回新引用**（多窗口同步的命门）。
 *
 * 契约在 `flowChanges.applyNodeChanges` 里（空变更原样返回、被碰到的
 * 元素是新对象、其余复用）。这里只保留这个名字：宿主、验证套件和
 * 文档都用它，改名的收益是零。
 */
export function publishNodeChanges(changes: FlowNodeChange[], prev: FlowNode[]): FlowNode[] {
  return applyNodeChanges(changes, prev)
}

/** 连线版本，理由同上 */
export function publishEdgeChanges(changes: FlowEdgeChange[], prev: FlowEdge[]): FlowEdge[] {
  return applyEdgeChanges(changes, prev)
}

/**
 * 「这份文档确实换过了」—— 给**直接改字段**的场合用的收尾动作。
 *
 * ⚠️ 什么时候需要它：宿主自己就地改了文档里的东西（比如
 * `node.selected = false`、`edge.data.x = …`），这时没有任何变更列表可合并，
 * 但别的窗口必须重新同步一次。只换那些被判定为「脏」的元素的引用。
 *
 * 传 `null` 表示整份都换（元素少、或者不确定哪些脏时用）。
 */
export function republish<T extends { id: string }>(
  list: T[],
  dirtyIds: Iterable<string> | null,
): T[] {
  if (dirtyIds === null) return list.map((item) => ({ ...item }))
  const touched = new Set(dirtyIds)
  return list.map((item) => (touched.has(item.id) ? { ...item } : item))
}
